import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { useNavigate } from "react-router-dom";
import {
  getAllNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../../../api/notificationApi";
import { useNotifications } from "../../contexts/NotificationContext";

const typeSeverity = {
  info: "info",
  success: "success",
  warning: "warning",
  error: "danger",
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { refresh: refreshContext } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = async (p = page, ps = pageSize) => {
    try {
      setLoading(true);
      const data = await getAllNotifications({ page: p, pageSize: ps });
      if (data?.success) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPage = (e) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    setPage(newPage);
    setPageSize(e.rows);
    load(newPage, e.rows);
  };

  const handleRowClick = async (rowData) => {
    if (!rowData.is_read) {
      await markNotificationRead(rowData.id);
      refreshContext();
    }
    if (rowData.link) navigate(rowData.link);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    await load();
    refreshContext();
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await deleteNotification(id);
    await load();
    refreshContext();
  };

  const typeBody = (row) => (
    <Tag severity={typeSeverity[row.type] || "info"} value={row.type} />
  );

  const titleBody = (row) => (
    <div>
      <div style={{ fontWeight: row.is_read ? 400 : 600 }}>{row.title}</div>
      {row.body && (
        <div style={{ color: "var(--app-text-secondary)", fontSize: "0.85rem" }}>
          {row.body}
        </div>
      )}
    </div>
  );

  const dateBody = (row) => new Date(row.createdAt).toLocaleString();

  const statusBody = (row) =>
    row.is_read ? (
      <Tag severity="secondary" value="Read" />
    ) : (
      <Tag severity="info" value="Unread" />
    );

  const actionsBody = (row) => (
    <Button
      icon="pi pi-trash"
      className="p-button-text p-button-rounded p-button-sm p-button-danger"
      onClick={(e) => handleDelete(row.id, e)}
    />
  );

  const header = (
    <div className="flex justify-content-between align-items-center">
      <h3 style={{ margin: 0 }}>All Notifications</h3>
      <Button
        label="Mark all read"
        icon="pi pi-check"
        className="p-button-sm"
        onClick={handleMarkAllRead}
      />
    </div>
  );

  return (
    <div className="p-3">
      <Card>
        <DataTable
          value={items}
          loading={loading}
          header={header}
          lazy
          paginator
          first={(page - 1) * pageSize}
          rows={pageSize}
          totalRecords={total}
          onPage={onPage}
          rowsPerPageOptions={[10, 20, 50]}
          onRowClick={(e) => handleRowClick(e.data)}
          emptyMessage="No notifications"
          rowClassName={(row) => (row.is_read ? "" : "font-bold")}
          selectionMode="single"
        >
          <Column field="type" header="Type" body={typeBody} style={{ width: "6rem" }} />
          <Column header="Message" body={titleBody} />
          <Column field="event" header="Event" style={{ width: "12rem" }} />
          <Column header="Status" body={statusBody} style={{ width: "7rem" }} />
          <Column header="Time" body={dateBody} style={{ width: "12rem" }} />
          <Column header="" body={actionsBody} style={{ width: "4rem" }} />
        </DataTable>
      </Card>
    </div>
  );
};

export default NotificationsPage;
